import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  createNewConsumer,
  createNewGroupConsumer,
} from "../../../../channels/consumer";
import SequenceTracks from "./SequenceTracks";

// Simple helper for truncating text in React:
const truncate = (str = "", length = 20) => {
  if (str.length <= length) return str;
  return str.substring(0, length) + "...";
};

function LastUpdated({
  initial_result,
  liveProgress,
  OnProgressUpdate,
  syncing_data,
}) {
  const formatDate = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      month: "long",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const [result, setResult] = useState(initial_result);
  const [promptIndex, setPromptIndex] = useState(0);

  const syncPrompts = [
    "Still talking to the server...",
    "Just a moment, gathering data...",
    "Making sense of the chaos...",
    "Reading student minds (just kidding)...",
    "Double-checking everything...",
    "One moment, organizing the mess...",
    "Polishing the progress report...",
    "Bringing order to student mayhem...",
    "Sync in progress — thanks for your patience.",
    "Almost like grading, but faster...",
  ];

  useEffect(() => {
    if (syncing_data) {
      const interval = setInterval(() => {
        setPromptIndex((prevIndex) => (prevIndex + 1) % syncPrompts.length);
      }, 2000);
      return () => clearInterval(interval);
    } else {
      const newResult = { ...liveProgress };
      newResult.updated_at = new Date().toISOString();
      setResult(newResult);
    }
  }, [syncing_data]);

  const handleRefreshProgress = useCallback(() => {
    OnProgressUpdate();
  }, []);

  return (
    <div className="text-center">
      <div className="padless marginless">
        <b>Last updated on:</b>{" "}
        {result ? <>{formatDate(result.updated_at)} </> : "Never"}
        <div className="sync-container">
          <button
            type="button"
            className="refresh-progress"
            onClick={handleRefreshProgress}
            disabled={syncing_data}
          >
            {syncing_data ? (
              <div className="spinner-container">
                <div className="spinner" />
                <div className="sync-prompt-wrapper">
                  <div className="sync-prompt">{syncPrompts[promptIndex]}</div>
                </div>
              </div>
            ) : (
              "Refresh Progress"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

const MappedSequenceProgressGrid = (props) => {
  const {
    users = [],
    progress = [],
    result = {},
    navigation = {},
    progress_url = "",
  } = props;

  const titleRef = useRef(null);
  const [isFlat, setIsFlat] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const [liveProgress, updateLiveProgress] = useState(progress);
  const [firstRequestProcessed, setFirstRequestProcessed] = useState(false);

  useEffect(() => {
    subscribeToMappedProgress();
    // Subscribe to users achievement channels.
    subscribeToUserAchievements();

    checkHeight();
  }, []);
  useEffect(() => {
    const observer = new ResizeObserver(checkHeight);
    if (titleRef.current) {
      observer.observe(titleRef.current);
    }
    checkHeight();
    return () => {
      observer.disconnect();
    };
  }, [liveProgress]);

  const checkHeight = useCallback(() => {
    if (titleRef.current) {
      // check if offset is small  to change.
      if (titleRef.current.offsetWidth < 64) {
        setIsFlat(false);
      } else {
        setIsFlat(true);
      }
    }
  }, [titleRef.current]);

  const subscribeToMappedProgress = () => {
    const sub = createNewGroupConsumer(
      users[0].token,
      navigation.groupId
    ).subscriptions.create(
      {
        channel: "MappedProgressChannel",
        id: users[0].id,
        groupId: navigation.groupId,
      },
      {
        // Called once when the subscription is created.
        initialized() {
          // Used for initialization
        },
        // Called when the subscription is ready for use on the server.
        connected() {
          OnProgressUpdate();
        },
        // Called when the WebSocket connection is closed.
        disconnected() {
          console.warn(
            users[0].id,
            ": Client disconnected to User Requirement Channel"
          );
        },
        // Called when the subscription is rejected by the server.
        rejected() {
          console.warn(
            users[0].id,
            ": Client rejected to User Requirement Channel"
          );
        },
        received(data) {
          console.info(`You received an update for the group: ${data}`, data);
          if (data.group_license.track_id != navigation.selectedTrackId) return;

          if (!firstRequestProcessed) {
            setFirstRequestProcessed(true);
          }

          // 1) Parse the incoming data array
          const progressData = JSON.parse(data.group_license.data);
          updateLiveProgress(progressData);
          setSyncing(false);
          window.dispatchEvent(new Event("resize"));
        },
      }
    );

    window.addEventListener("beforeunload", () => {
      sub.consumer.disconnect();
    });
  };

  const subscribeToUserAchievements = () => {
    for (let i = 0; i < users.length; i++) {
      const user = users[i];

      if (user.token) {
        const sub = createNewConsumer(user.token).subscriptions.create(
          { channel: "UserRequirementChannel", id: user.id },
          {
            initialized() {
              // Used for initialization
            },
            connected() {
              // Subscribed
            },
            disconnected() {
              console.warn(
                user.id,
                ": Client disconnected to User Requirement Channel"
              );
            },
            rejected() {
              console.warn(
                user.id,
                ": Client rejected to User Requirement Channel"
              );
            },
            received(data) {
              console.info(
                `you received an update from user: ${data.user_id}`,
                data
              );
              // Use a functional update to ensure you're working with the latest state
              updateLiveProgress((prevLiveProgress) => {
                // Map through the previous state to create a new updated array
                const clonedProgress = [...prevLiveProgress];
                clonedProgress.map((activity) => {
                  const p = activity.table;
                  if (data.requirement_id != null && p.requirement_id != null) {
                    if (p.requirement_id == data.requirement_id) {
                      p.user_requirement_data.push({
                        user_id: data.user_id,
                        status: 3,
                      });
                    }
                  } else {
                    if (p.elemental_id == data.element_id) {
                      if (data.type == "QuizElement") {
                        let isBetter = true;
                        for (
                          let index = 0;
                          index < p.user_quiz_data.length;
                          index++
                        ) {
                          const element = p.user_quiz_data[index];
                          if (element.user_id == data.user_id) {
                            if (element.score >= data.score) {
                              isBetter = false;
                            } else {
                              element.score = data.score;
                            }
                          }
                        }
                        if (isBetter) {
                          if (data.complete) {
                            p.user_requirement_data.push({
                              user_id: data.user_id,
                              status: 3,
                            });
                            p.user_quiz_data.push({
                              user_id: data.user_id,
                              score: data.score,
                            });
                          } else {
                            p.user_quiz_data.push({
                              user_id: data.user_id,
                              score: data.score,
                            });
                          }
                        }
                      } else {
                        p.user_requirement_data.push({
                          user_id: data.user_id,
                          status: 3,
                        });
                      }
                    }
                  }
                });
                return clonedProgress;
              });
            },
          }
        );

        window.addEventListener("beforeunload", () => {
          sub.consumer.disconnect();
        });
      }
    }
  };

  // For the “user” column:
  const renderUserColumn = () => {
    return (
      <div className="user-col">
        {/* Top cell: "Click user for report" */}
        <div
          className="first-cell card inactive expand user-cell sticky-top"
          style={{ zIndex: 10 }}
        >
          Click user for report
        </div>

        {users.map((user, index) => {
          // Build the link path (or pass in from props if preferred)
          const userLink = `/u/groups/${navigation.groupId}/licenses/${navigation.licenseId}/mapped_sequence_progress/${navigation.selectedTrackId}/individual/${user.id}`;

          return (
            <a
              key={index + " " + user.username}
              href={userLink}
              target="_blank"
              rel="noopener noreferrer"
              className="progress-cell-link"
            >
              <div
                className="card expand bordered bordered-hoverable user-cell sticky-left"
                style={{ zIndex: 5 }}
              >
                <div className="primary-text">
                  <b>{truncate(user.username, 20)}</b>
                </div>
                <div className="screen-m-up">
                  {truncate(`${user.first_name} ${user.last_name}`, 20)}
                </div>
              </div>
            </a>
          );
        })}
      </div>
    );
  };

  // For the “progress” columns:
  const renderProgressColumns = () => {
    if (
      (!liveProgress.length && !firstRequestProcessed) ||
      liveProgress == undefined
    ) {
      // If no activities, match the original "No requirable activities" logic:
      return (
        <div className="f-col-s12 flex f-d-column padless">
          <div className="card inactive text-center progress-cell">
            Loading Activities. Please wait...
          </div>
          {users.map((_, idx) => (
            <div
              key={idx + "prog"}
              className="card inactive progress-cell text-center"
            >
              {/* Just a blank cell per user */}
              &nbsp;
            </div>
          ))}
        </div>
      );
    } else if (!liveProgress.length) {
      // If no activities, match the original "No requirable activities" logic:
      return (
        <div className="f-col-s12 flex f-d-column padless">
          <div className="card inactive text-center progress-cell">
            No requirable activities on this page
          </div>
          {users.map((_, idx) => (
            <div
              key={idx + "prog"}
              className="card inactive progress-cell text-center"
            >
              {/* Just a blank cell per user */}
              &nbsp;
            </div>
          ))}
        </div>
      );
    }

    return liveProgress.map((activity, colIndex) => {
      const p = activity.table ? activity.table : activity;
      // Determine which icon to show:
      let iconClass = "bx"; // fallback
      switch (p.elemental_type) {
        case "CmraBlocksElement":
          iconClass = "bx bx-laptop ";
          break;
        case "ArtifactElement":
          iconClass = "bx bxs-file-image";
          break;
        case "QuizElement":
          iconClass = "bx bxs-calendar-edit";
          break;
        default:
          iconClass = "bx bx-book";
          break;
      }

      // Show only the last part of the title after a colon:
      const shortTitle = p.title.includes(":")
        ? p.title.split(":").pop()
        : p.title;

      return (
        <div
          key={p.id + " " + colIndex}
          className="f-col-s12 flex f-d-column padless"
          ref={colIndex == 0 ? titleRef : null}
        >
          {/* Sticky top cell: show only the icon, shortTitle as tooltip */}

          {isFlat ? (
            <ActivityTitleFlat
              key={p.id + "_activity"}
              iconClass={iconClass}
              shortTitle={shortTitle}
            />
          ) : (
            <ActivityTitle
              key={p.id + "_activity"}
              iconClass={iconClass}
              shortTitle={shortTitle}
              isLast={colIndex === liveProgress.length - 1}
            />
          )}

          {/* For each user, show his or her status or score */}
          {users.map((user, index) => {
            // Build the same link for user + activity
            const userLink = `/u/groups/${navigation.groupId}/licenses/${navigation.licenseId}/mapped_sequence_progress/${navigation.selectedTrackId}/individual/${user.id}`;
            const isCompleted = () => {
              for (
                let index = 0;
                index < p.user_requirement_data.length;
                index++
              ) {
                const element = p.user_requirement_data[index];
                if (user.id == element.user_id && element.status == 3) {
                  return true;
                }
              }
              return false;
            };

            const isScorable = () => {
              return p.elemental_type == "QuizElement" ? true : false;
            };

            const hasAttempted = () => {
              if (!isScorable()) return false;
              for (let index = 0; index < p.user_quiz_data.length; index++) {
                const element = p.user_quiz_data[index];
                if (user.id == element.user_id) {
                  return true;
                }
              }
              return false;
            };
            const userScore = () => {
              if (!hasAttempted()) return "*";
              if (!isScorable()) return false;
              for (let index = 0; index < p.user_quiz_data.length; index++) {
                const element = p.user_quiz_data[index];

                if (user.id == element.user_id) {
                  return element.score + "%";
                }
              }
            };

            const iconStatus = {
              backgroundColor: isCompleted() ? "limegreen" : "none",
              display: "flex",
            };

            // Decide cell style & text:
            let cardClass =
              "card progress-cell bordered bordered-box t-hitbox text-center";
            let displayContent = (
              <span key={index + " " + user.id} style={iconStatus}>
                <i className={`${iconClass} `} style={{ color: "gray" }} />
              </span>
            );

            if (isCompleted() && !isScorable()) {
              cardClass =
                "card progress-completed  progress-cell bordered bordered-box text-center t-hitbox";
              displayContent = (
                <span key={index + " " + user.id} style={iconStatus}>
                  <i className={`${iconClass}`} />
                </span>
              );
            } else if (isCompleted() && isScorable()) {
              cardClass =
                "card progress-completed progress-cell bordered bordered-box text-center t-hitbox";
              displayContent = `${userScore()}`;
            } else if (!isCompleted() && isScorable() && hasAttempted()) {
              cardClass =
                "card progress-incomplete progress-cell bordered bordered-box text-center t-hitbox";
              displayContent = `${userScore()}`;
            }

            return (
              <a
                key={`${p.id}-${user.id}`}
                href={userLink}
                target="_blank"
                rel="noopener noreferrer"
                className="progress-cell-link"
              >
                <div
                  key={index + " " + user.id}
                  className={cardClass}
                  data-hitbox_id={colIndex}
                  data-tooltip={`${user.first_name} ${user.last_name}: \n ${shortTitle}`}
                >
                  {displayContent}
                </div>
              </a>
            );
          })}
        </div>
      );
    });
  };

  const OnProgressUpdate = useCallback(() => {
    console.info(`Requesting data from ${progress_url}`);
    setSyncing(true);
    fetch(progress_url).then((r) => {});
  }, [progress_url]);

  return (
    <div className="mapped-progress-table">
      <div className="mapped-sequence-view">
        <LastUpdated
          initial_result={result}
          liveProgress={liveProgress}
          OnProgressUpdate={OnProgressUpdate}
          syncing_data={syncing}
        />
        {/* SequenceTracks is unchanged, just passing in navigation */}
        {navigation.tracks.length > 0 && (
          <SequenceTracks navigation={navigation} />
        )}

        <div className="mapped-progress-table-columns">
          {/* First column: User list */}
          {renderUserColumn()}

          {/* Additional columns: Each "progress" item */}
          <div className="progress-cols">{renderProgressColumns()}</div>
        </div>
      </div>
    </div>
  );
};
const ActivityTitleFlat = (props) => {
  return (
    <div className="progress-title-cell-horizontal ">
      <div className="description-box-wrapper">
        <div className="activity-description-horizontal inactive">
          {props.shortTitle}
        </div>
      </div>
    </div>
  );
};
const ActivityTitle = (props) => {
  const { shortTitle, isLast, sentToGoogle, googleWorkFound, iconClass } =
    props;
  const activityTitleRef = useRef(null);
  const triRef = useRef(null);
  const descriptionRef = useRef(null);
  const [descHeight, setDescHeight] = useState(0);
  const [topTriangle, setTopTriangle] = useState(0);
  const [leftTriangle, setLeftTriangle] = useState(0);
  const [leftDescription, setLeftDescription] = useState("25px");

  useEffect(() => {
    const updateMeasurements = () => {
      if (activityTitleRef.current) {
        const w = activityTitleRef.current.offsetWidth;
        // or use getBoundingClientRect().width if you prefer
        const half = w / 2;
        // sqrt( (w/2)^2 + (w/2)^2 ) = w / sqrt(2)
        const newHeight = Math.sqrt(half * half + half * half);
        setDescHeight(newHeight);
      }
      if (triRef.current) {
        setLeftDescription(triRef.current.clientWidth);
      }
      if (descriptionRef.current) {
        const bounds = descriptionRef.current.getBoundingClientRect();

        const top = bounds.width - activityTitleRef.current.clientHeight;

        setTopTriangle(-top);
        setLeftTriangle(bounds.width);
      }
    };

    // Run once on mount
    setTimeout(() => updateMeasurements(), 100);

    // If the width might change on resize, add a listener
    window.addEventListener("resize", updateMeasurements);
    return () => window.removeEventListener("resize", updateMeasurements);
  }, []);

  return (
    <div
      className="progress-title-cell transparent"
      title={shortTitle}
      ref={activityTitleRef}
      key={"activity" + shortTitle}
    >
      <div className="description-box-wrapper">
        <div
          ref={descriptionRef}
          className="activity-description-box"
          style={{
            height: `${descHeight}px`,
          }}
        >
          <div className="activity-description inactive">{shortTitle}</div>
        </div>
      </div>
      {
        <div
          className="flatBox inactive"
          style={{
            top: `${topTriangle - 2}px`, // add 2 for border
            left: `${leftTriangle + 2}px`,
            // clipPath: isLast ? "polygon(100% 0, 35px 100%, 0 0)" : "none",
          }}
        />
      }
      <div className="triangle-bg" ref={triRef}>
        <div className="triangle-top inactive" />
      </div>
    </div>
  );
};
export default MappedSequenceProgressGrid;
